import { create } from '@bufbuild/protobuf';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { agentTokenApi } from '@/api/agentToken.api';
import { ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';
import {
  type CreateAgentTokenRequest,
  type UpdateAgentTokenRequest,
} from '@/proto/agentplatform/httpv2/token_pb';
import { TokenType } from '@/proto/agentplatform/v2/token_pb';

const tokenQk = (type: TokenType, page?: number, pageSize?: number, name = '') =>
  page === undefined
    ? (['agent-tokens', 'list', type] as const)
    : (['agent-tokens', 'list', type, page, pageSize, name] as const);

export function useAgentTokens(type: TokenType, page = 1, pageSize = 10, enabled = true, name = '') {
  return useQuery({
    queryKey: tokenQk(type, page, pageSize, name),
    queryFn: () => {
      // AgentTokenModel.type 为字符串列，存 proto 枚举名（如 "API"）；
      // UNSPECIFIED 时不传 filter，与旧 bespoke 入参语义一致。
      const filter: Record<string, string> = {};
      if (type !== TokenType.TOKEN_TYPE_UNSPECIFIED) {
        filter.type = TokenType[type];
      }
      return agentTokenApi.list(
        create(ListRequestSchema, { page, pageSize, filter, fuzzy: name ? { name } : {} })
      );
    },
    enabled,
  });
}

export function useCreateAgentToken(type: TokenType) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAgentTokenRequest) => agentTokenApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tokenQk(type) });
    },
  });
}

export function useUpdateAgentToken(type: TokenType) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateAgentTokenRequest) => agentTokenApi.update(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tokenQk(type) });
    },
  });
}

export function useDeleteAgentToken(type: TokenType) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => agentTokenApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tokenQk(type) });
    },
  });
}

export function useRegenerateAgentToken(type: TokenType) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => agentTokenApi.regenerate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tokenQk(type) });
    },
  });
}

export function useToggleAgentToken(type: TokenType) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; enable: boolean }) => {
      if (vars.enable) {
        await agentTokenApi.enable(vars.id);
      } else {
        await agentTokenApi.disable(vars.id);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tokenQk(type) });
    },
  });
}
